import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  BranchPayload,
  BranchRecord,
  listBranches,
  saveBranch,
} from "../../../api/branches";
import { usePermissions } from "../../../hooks/usePermissions";

const BRANCHES_SCREEN_ID = "154";

type ModalMode = "add" | "edit" | null;

const emptyForm: BranchPayload = { name: "", mobile: "" };

export const BranchesSettingsPanel: React.FC = () => {
  const { hasPermission } = usePermissions();
  const canAdd = hasPermission(BRANCHES_SCREEN_ID, "Add");
  const canEdit = hasPermission(BRANCHES_SCREEN_ID, "Edit");

  const [branches, setBranches] = useState<BranchRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editId, setEditId] = useState<string | undefined>();
  const [form, setForm] = useState<BranchPayload>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const reload = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const rows = await listBranches();
      setBranches(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load branches.");
      setBranches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditId(undefined);
    setFormError("");
    setInfo("");
    setModalMode("add");
  };

  const openEdit = (row: BranchRecord) => {
    setForm({ name: row.name, mobile: row.mobile });
    setEditId(row.id);
    setFormError("");
    setInfo("");
    setModalMode("edit");
  };

  const closeModal = () => {
    if (saving) return;
    setModalMode(null);
    setFormError("");
  };

  const handleSave = async () => {
    setFormError("");
    setSaving(true);
    try {
      const saved = await saveBranch(form, {
        id: editId,
        isEdit: modalMode === "edit",
      });
      setModalMode(null);
      if (modalMode === "add") {
        setInfo(
          `Branch "${saved.name}" created. System ledgers (Round Off, Cash, Sales, VAT, …) were seeded for this branch.`
        );
      } else {
        setInfo(`Branch "${saved.name}" updated.`);
      }
      await reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Failed to save branch.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Branches
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage branches here. Creating a branch also creates system ledgers
            for that branch (same names, unique ids).
          </p>
        </div>
        {canAdd && (
          <button
            type="button"
            onClick={openCreate}
            className="shrink-0 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            Create branch
          </button>
        )}
      </div>

      {info ? (
        <div className="rounded-lg border border-success-200 bg-success-50 dark:bg-success-900/20 px-3 py-2 text-sm text-success-800 dark:text-success-200">
          {info}
        </div>
      ) : null}

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800/80">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium text-gray-600 dark:text-gray-300">
                Name
              </th>
              <th className="px-4 py-2.5 text-left font-medium text-gray-600 dark:text-gray-300">
                Mobile
              </th>
              <th className="px-4 py-2.5 text-left font-medium text-gray-600 dark:text-gray-300">
                Status
              </th>
              <th className="px-4 py-2.5 text-right font-medium text-gray-600 dark:text-gray-300">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-900">
            {loading ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                >
                  Loading branches…
                </td>
              </tr>
            ) : branches.length === 0 ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                >
                  No branches found.
                </td>
              </tr>
            ) : (
              branches.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-2.5 text-gray-900 dark:text-gray-100 font-medium">
                    {row.name || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                    {row.mobile || "—"}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                        row.status === "Active"
                          ? "bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300"
                          : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {canEdit ? (
                      <button
                        type="button"
                        onClick={() => openEdit(row)}
                        className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                      >
                        Edit
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalMode &&
        createPortal(
          <div className="fixed inset-0 z-[10060] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={closeModal}
            />
            <div className="relative w-full max-w-md rounded-xl bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 p-5">
              <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {modalMode === "add" ? "Create branch" : "Edit branch"}
              </h4>
              {modalMode === "add" ? (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  System ledgers will be created automatically for this branch.
                </p>
              ) : null}

              <div className="mt-4 space-y-3">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Name *
                  </span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    placeholder="Enter branch name"
                    disabled={saving}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Mobile *
                  </span>
                  <input
                    type="text"
                    value={form.mobile}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, mobile: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    placeholder="Enter mobile number"
                    disabled={saving}
                  />
                </label>
              </div>

              {formError ? (
                <p className="mt-3 text-sm text-red-600 dark:text-red-400">
                  {formError}
                </p>
              ) : null}

              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void handleSave()}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg bg-success-500 text-white text-sm font-medium hover:bg-success-600 disabled:opacity-70"
                >
                  {saving ? "Saving…" : "Submit"}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
